import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SafeUrl } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { ProfilePhotoService } from './profile-photo.service';

/** "Dana Cole" → "DC", "dana@x.com" → "D". */
export function avatarInitials(name: string | null | undefined): string {
  const parts = (name || '').trim().split(/\s+/).filter(p => p.length > 0);
  if (parts.length === 0) {
    return '';
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

/**
 * A person's avatar: their profile photo, or their initials on navy when they have none
 * (or while it loads). Sizes: sm 32 px, md 40 px (table rows), nav 36 px, lg 56 px.
 */
@Component({
  selector: 'app-user-avatar',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="user-avatar" [ngClass]="'user-avatar--' + size" [class.user-avatar--muted]="muted"
      [attr.role]="label ? 'img' : null" [attr.aria-label]="label || null" [attr.aria-hidden]="label ? null : 'true'">
      <img *ngIf="photo$ | async as src; else initialsTemplate" [src]="src" alt="" draggable="false" />
      <ng-template #initialsTemplate><span class="user-avatar__initials">{{ shownInitials }}</span></ng-template>
    </span>
  `,
  styles: [`
    :host { display: inline-flex; flex-shrink: 0; }
    .user-avatar {
      display: inline-flex; align-items: center; justify-content: center; overflow: hidden;
      border-radius: 9999px; background: #0c4a79; color: #fff; font-weight: 600;
      letter-spacing: 0.02em; line-height: 1; text-transform: uppercase; user-select: none;
    }
    .user-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .user-avatar--sm { width: 2rem; height: 2rem; font-size: 0.75rem; }
    .user-avatar--nav { width: 2.25rem; height: 2.25rem; font-size: 0.8125rem; }
    .user-avatar--md { width: 2.5rem; height: 2.5rem; font-size: 0.875rem; font-weight: 700; }
    .user-avatar--lg { width: 3.5rem; height: 3.5rem; font-size: 1.25rem; }
    .user-avatar--muted { background: #d1d5db; }
    .user-avatar--muted img { filter: grayscale(0.6); opacity: 0.75; }
  `],
})
export class UserAvatarComponent implements OnChanges {
  /** Whose photo to show. */
  @Input() userId: string | null | undefined;
  /** Used for the initials when `initials` isn't given. */
  @Input() name: string | null | undefined;
  /** Overrides the initials worked out from `name`. */
  @Input() initials: string | null | undefined;
  @Input() size: 'sm' | 'nav' | 'md' | 'lg' = 'md';
  /** Greyed out (e.g. no activity on Admin › Usage). */
  @Input() muted = false;
  /** Accessible name; leave empty when the name is already next to the avatar. */
  @Input() label: string | null | undefined;

  photo$: Observable<SafeUrl | null> = of(null);
  shownInitials = '';

  constructor(private photos: ProfilePhotoService) { }

  ngOnChanges(): void {
    this.shownInitials = (this.initials || avatarInitials(this.name) || '?').slice(0, 2);
    this.photo$ = this.photos.photoUrl(this.userId);
  }
}
